"use client";

/**
 * The config-driven lead form renderer.
 *
 * This is the piece that makes everything else visible: it reads a `lead_form_config`
 * row's `field_schema` and renders the journey the CMS author assembled — N steps, a
 * 12-column grid, conditional fields, OTP gates, live option sources — with no component
 * per variant and no code shipped for a new form.
 *
 * ─── Rules are evaluated by the SHARED module, never re-implemented ──────────────────
 * `@shared/leadFormRules` resolves to a generated, drift-checked copy of
 * `server/lib/leadFormRules.js` — the same evaluator, byte for byte, that the server
 * validates with. A second evaluator here would be the worst kind of bug: the two would
 * agree on every case an author tests and disagree on one edge, producing a form that looks
 * green in the builder and cannot be submitted by a real visitor — visible only in
 * production lead data. It is a copy and not the file itself only because CI builds this
 * app with `docker build ./webapp`, which puts `server/` outside the build context; the
 * copy cannot drift, because `webapp/scripts/check-shared-sync.mjs` fails the build if it
 * does. See that script, next.config.mjs and src/types/sharedLeadFormRules.d.ts.
 *
 * ─── Behaviour here, appearance in the skin ─────────────────────────────────────────
 * Every visual decision comes from a `LeadFormSkinDefinition` class bundle. This file
 * holds ALL the behaviour and none of the look, so adding a skin is a styling exercise
 * that cannot introduce a sequencing or validation bug. See ./skins/types.ts.
 *
 * ─── The client is not trusted, and does not pretend to be ──────────────────────────
 * Everything here is re-decided server-side: visibility, required-ness, the OTP gate, and
 * which columns a value may reach. Client-side evaluation exists so the form REACTS
 * instantly, not so it can be believed. A step rejected by the server wins, and its
 * per-field errors are rendered as-is.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isSpacerField,
  resolveFieldStates,
  resolveSubmitStepIndex,
  visibleFieldsForStep,
  type LeadFormFieldSchema,
  type LeadFormStepSchema,
} from "@shared/leadFormRules";

import { cn, setCookie } from "@/lib/utils";
import {
  trackLeadAoi,
  trackLeadHq,
  trackLeadName,
  trackLeadSubmit,
  trackLeadSubmitFail,
  trackLeadformEmail,
  trackLeadformError,
  trackLeadformFillstart,
  trackLeadformMobile,
  trackWidgetImpression,
} from "@/customAnalytics/rudderStackEvents";
import { identifyUser } from "@/customAnalytics/rudderStackEvents";
import { authSuccess } from "@/customAnalytics/trackingEvents";
import { WIDGET_NAMES } from "@/customAnalytics/widgetNames";
import { handleDownload, pushToGTMHashed } from "@/utils/utils";
import {
  completeLeadForm,
  resolveLeadFormOptions,
  sendLeadFormOtp,
  submitLeadFormStep,
  verifyLeadFormOtp,
  type LeadFormOption,
  type OtpRequired,
} from "@/api/leadForm";
import Image from "next/image";
import { PopoverSelect } from "@/components/atoms/PopoverSelect";
import { renderConsentText } from "@/lib/consentText";
import { readLeadAttribution } from "@/lib/leadAttribution";
import { resolveLeadFormSkin } from "./skins";
import { resolveStepOverrides } from "./skins/stepSettings";
import { useLeadFormSession } from "./useLeadFormSession";
import { resolveSkinValues, type LeadFormSkinDefinition, type LeadFormSkinValues } from "./skins/types";

export interface ConfigLeadFormProps {
  configId: number;
  universityId: number;
  schema: { version?: number; steps?: LeadFormStepSchema[] } | null | undefined;
  /** Skin id (`lead_form_config.template_key`). Unknown values fall back to the default. */
  skinId?: string | null;
  /**
   * Which layout to render. NOT a width switch — the skin declares two different trees,
   * because a popup and an embedded form are genuinely different compositions.
   */
  surface?: "fixed" | "popup";
  /** `lead_form_config.copy_config` — the author's words. */
  copyConfig?: Record<string, unknown> | null;
  /** `lead_form_config.theme_config` — the author's colours, links and images. */
  themeConfig?: Record<string, unknown> | null;
  /** Popup only: closes the dialog. Without it no close affordance is rendered. */
  onClose?: () => void;
  /**
   * Attribution carried on the first submit; the CRM builders read it off the row.
   *
   * The visitor's own UTM parameters are read from the URL automatically — a host does not
   * need to pass them. Anything supplied here is merged OVER that, so a widget with better
   * information (a known placement, a campaign it was rendered for) can say so, and a host
   * that knows nothing can keep passing nothing.
   */
  context?: Record<string, unknown>;
  /** Marks the CRM drop as a preview. Still creates a real lead — see `previewOnly`. */
  isPreview?: boolean;
  /**
   * Admin preview: render and step through WITHOUT touching the network.
   *
   * Distinct from `isPreview`, and the distinction matters. `isPreview` still mints a lead
   * row and still pushes to the CRM with a preview flag; an admin flipping through skins
   * must do neither, or the CRM fills with junk and real OTP texts go to whatever number
   * was typed. Preview-only short-circuits every call.
   */
  previewOnly?: boolean;
  /**
   * Preview only: jump straight to a step.
   *
   * Without this the only way to see step 2 is to fill in step 1, and an OTP gate is
   * unreachable entirely — so an author styling a three-step journey could only ever
   * inspect a third of it. Ignored outside `previewOnly`, where step order is the
   * server's business, not the caller's.
   */
  previewStepIndex?: number;
  /**
   * Preview only: force a terminal screen. `steps.length` and beyond means "finished", and
   * this says WHICH ending to show — you cannot reach the returning-user or download screen
   * by clicking through, so without it two of the three are unconfigurable.
   */
  previewOutcome?: LeadFormOutcome;
  /**
   * How this form was opened. `download` makes the ending a brochure screen and starts the
   * file — the same distinction `LEAD_DROP_VARIANTS.DOWNLOAD` draws for the legacy forms.
   */
  variant?: string;
  /** The brochure to hand over when `variant` is a download. */
  downloadLink?: string;
  onComplete?: (result: { leadId: string; leadsId: string | null }) => void;
  className?: string;
}

/** Vertical spacing scale — mirrors LEAD_FORM_SPACING_TOKENS on the server. */
const SPACING_TOKENS: Record<string, number> = {
  none: 0, xs: 4, sm: 8, md: 16, lg: 24, xl: 32, "2xl": 48,
};

const spacerHeight = (value: string | number | undefined): number => {
  if (value == null || value === "") return SPACING_TOKENS.md;
  if (typeof value === "number") return Number.isInteger(value) && value >= 0 ? value : 0;
  if (Object.prototype.hasOwnProperty.call(SPACING_TOKENS, value)) return SPACING_TOKENS[value];
  const n = Number(value);
  return Number.isInteger(n) && n >= 0 ? n : 0;
};

/** Tailwind cannot see a computed `col-span-${n}`, so the classes must be literal. */
const COL_SPAN_CLASS: Record<number, string> = {
  1: "col-span-1", 2: "col-span-2", 3: "col-span-3", 4: "col-span-4",
  5: "col-span-5", 6: "col-span-6", 7: "col-span-7", 8: "col-span-8",
  9: "col-span-9", 10: "col-span-10", 11: "col-span-11", 12: "col-span-12",
};

const colSpanClass = (span: number | undefined) =>
  COL_SPAN_CLASS[Math.min(12, Math.max(1, Number(span) || 12))] ?? "col-span-12";

const OTP_LENGTH = 4;
/** Same lifetime lead form 8 gives the OMS token — 131400 minutes. */
const OMS_TOKEN_COOKIE_MAX_AGE_SEC = 131400 * 60;
const FORM_NAME = WIDGET_NAMES.LEAD_FORM_CUSTOM;

/**
 * How a journey ENDS. Three of them, because "finished" is not one outcome: telling a
 * returning visitor "thank you for registering" is wrong, and a download screen has to
 * explain that a file is on its way.
 */
export type LeadFormOutcome = "new" | "existing" | "download";

/** Matches LEAD_DROP_VARIANTS.DOWNLOAD, without importing the whole legacy constant set. */
const DOWNLOAD_VARIANT = "download";

/**
 * Catalog key → the RudderStack event that field owns.
 *
 * The tracking sheet names five specific lead fields; everything else is covered by the
 * generic `leadform_fillstart`. Keying off the CATALOG entry rather than the field id is
 * what makes this survive an author renaming a field — the id is theirs to choose, the
 * catalog key is not.
 */
const TRACKED_CATALOG_FIELDS: Record<string, "mobile" | "name" | "email" | "hq" | "aoi"> = {
  mobile: "mobile",
  name: "name",
  email: "email",
  highest_qualification: "hq",
  area_of_interest: "aoi",
};

/**
 * Pull the identity fields the downstream analytics contracts need out of the answers.
 *
 * Keyed by CATALOG entry, not field id: `authSuccess`, `identifyUser` and the GTM push all
 * expect specific concepts (email, phone, name, …), and an author is free to give those
 * fields any id they like. A custom form may legitimately collect none of them, which is
 * why every value is optional and the callers below guard on what they got.
 */
const identityFromAnswers = (
  steps: LeadFormStepSchema[],
  answers: Record<string, unknown>,
) => {
  const byCatalog: Record<string, string> = {};
  for (const step of steps) {
    for (const f of Array.isArray(step.fields) ? step.fields : []) {
      if (!f?.catalog) continue;
      const raw = answers[f.id];
      if (raw === undefined || raw === null || raw === "") continue;
      byCatalog[f.catalog] = String(raw);
    }
  }
  return {
    email: byCatalog.email ?? "",
    phone: byCatalog.mobile ?? "",
    name: byCatalog.name ?? "",
    qualification: byCatalog.highest_qualification ?? "",
    interest: byCatalog.area_of_interest ?? "",
  };
};

/** One form-fill session = one lead row, even if the visitor edits their mobile mid-fill. */
const newSessionId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `cs-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

const ConfigLeadForm = ({
  configId,
  universityId,
  schema,
  skinId,
  surface = "fixed",
  copyConfig,
  themeConfig,
  onClose,
  context,
  isPreview,
  previewOnly = false,
  previewStepIndex,
  previewOutcome,
  variant,
  downloadLink,
  onComplete,
  className,
}: ConfigLeadFormProps) => {
  const skin: LeadFormSkinDefinition = useMemo(() => resolveLeadFormSkin(skinId), [skinId]);

  /**
   * Read once per mount, not per submit: the values must describe the URL the visitor
   * ARRIVED on, and a client-side route change would otherwise rewrite the attribution of
   * a form they are halfway through filling in.
   */
  const attribution = useMemo(() => readLeadAttribution(), []);
  const submitContext = useMemo(
    () => ({ ...attribution, ...(context ?? {}) }),
    [attribution, context],
  );
  const c = skin.classes;
  /**
   * The author's settings, merged over the skin's declared defaults. Resolved once here so
   * every read below is a plain property access and a missing value can never render as
   * `undefined` on screen.
   */
  const v: LeadFormSkinValues = useMemo(
    () => resolveSkinValues(skin, copyConfig, themeConfig),
    [skin, copyConfig, themeConfig],
  );

  const steps = useMemo<LeadFormStepSchema[]>(
    () => (Array.isArray(schema?.steps) ? (schema!.steps as LeadFormStepSchema[]) : []),
    [schema],
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [leadId, setLeadId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  /**
   * Which ending to show. Defaults to "new" and is narrowed on the way out: a download CTA
   * pins it to "download", and an OTP verify that reports `firstVerification === false`
   * means this mobile was already registered.
   */
  const [outcome, setOutcome] = useState<LeadFormOutcome>(
    String(variant ?? "").toLowerCase() === DOWNLOAD_VARIANT ? "download" : "new",
  );

  /**
   * The code as FOUR SLOTS, not one string.
   *
   * A collapsed string cannot say which box a digit belongs to: clear the second box of
   * "1234" and the remaining "134" re-renders as 1-3-4-blank, silently shuffling digits
   * the visitor never touched. `otp` below is the joined value the API wants; anything
   * short of four characters simply is not submittable.
   */
  const [otpDigits, setOtpDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(""));
  const otp = otpDigits.join("");
  const clearOtp = useCallback(() => setOtpDigits(Array(OTP_LENGTH).fill("")), []);
  const [otpSentFor, setOtpSentFor] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  const sessionIdRef = useRef<string>("");
  if (!sessionIdRef.current) sessionIdRef.current = newSessionId();

  /**
   * ─── The visitor's session ────────────────────────────────────────────────────────
   *
   * Everything a hand-built lead drop does between page loads: prefill from the cookie,
   * skip a visitor who has already verified, capture a partial fill, and write the session
   * back on success. See `useLeadFormSession` for why none of it can key off field ids.
   *
   * Disabled in preview because every one of those has a side effect outside this form —
   * a real `/leads/capture` row, a cookie that convinces the rest of the site the AUTHOR is
   * a verified visitor. Clicking through a draft must not do any of that.
   */
  const sessionEnabled = !previewOnly && isPreview !== true;

  /** Fill blanks only. A prefill must never overwrite something the visitor has typed. */
  const applySeed = useCallback((seed: Record<string, unknown>) => {
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [fieldId, value] of Object.entries(seed)) {
        const current = next[fieldId];
        if (current === undefined || current === null || current === "") {
          next[fieldId] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  /**
   * A visitor the browser says is already verified goes straight to the ending.
   *
   * This is variant 8's behaviour and the reason a returning visitor is not asked to prove
   * the same number twice. The outcome is "existing", never "new" — telling someone who
   * registered last week "thank you for registering" is the wrong sentence — but a download
   * CTA still wins, because they came for the brochure and still need it.
   */
  const showVerifiedEnding = useCallback(() => {
    setOutcome((prev) => (prev === "download" ? prev : "existing"));
    setDone(true);
  }, []);

  const session = useLeadFormSession({
    steps,
    answers,
    applySeed,
    onVerifiedVisitor: showVerifiedEnding,
    enabled: sessionEnabled,
    bindCourseToPage: skin.bindsCourseToPage === true,
  });

  // Follow the caller's step while previewing. Guarded on previewOnly so a live form can
  // never be driven from outside — that would let a page skip a gate.
  useEffect(() => {
    if (!previewOnly || previewStepIndex === undefined) return;
    // An index AT OR PAST the end means "show the ending". Clamping it to the last step —
    // which an earlier version did — made the Success pill silently render the OTP step.
    if (previewStepIndex >= steps.length) {
      setDone(true);
      if (previewOutcome) setOutcome(previewOutcome);
      return;
    }
    setStepIndex(Math.max(0, previewStepIndex));
    setDone(false);
  }, [previewOnly, previewStepIndex, previewOutcome, steps.length]);

  const rootRef = useRef<HTMLDivElement>(null);
  /** True while this component is on screen — see the options effect for why. */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const loadedKeys = useRef<Set<string>>(new Set());
  /** Fields already reported to `leadform_fillstart`; the event is once per field. */
  const fillStarted = useRef<Set<string>>(new Set());

  const step = steps[stepIndex];
  const isOtpStep = step?.kind === "otp";

  /**
   * The masked number the gate names back to the visitor.
   *
   * An OTP step declares WHICH field it verifies (`verifies`), so the number comes from
   * that answer rather than from a hardcoded field id — a form whose phone question is
   * called something else still shows the right digits. Masked the way lead form 9 masks
   * it: country code, seven X's, last three digits.
   */
  const otpTargetValue = String(
    (step?.verifies ? answers[step.verifies] : "") ?? "",
  ).replace(/\D/g, "");
  const maskedPhone = `+91 XXXXXXX${otpTargetValue.slice(-3)}`;

  const otpBoxRefs = useRef<(HTMLInputElement | null)[]>([]);

  /** Type a digit → fill that box and move on. Non-digits are dropped, never shown. */
  const handleOtpDigit = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    setFormError(null);
    if (digit && index < OTP_LENGTH - 1) otpBoxRefs.current[index + 1]?.focus();
  };

  /**
   * Paste fills every box.
   *
   * Without this the whole code lands in whichever box had focus and the visitor has to
   * retype it — the single most common thing people do with an SMS code.
   */
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setOtpDigits(
      Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] ?? ""),
    );
    setFormError(null);
    otpBoxRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  /**
   * "Change Number" — back to the step that ASKS for the verified field.
   *
   * Not `stepIndex - 1`: the gate need not sit directly after the step carrying the
   * number, and a conditional step in between would land the visitor somewhere arbitrary.
   * Clearing `otpSentFor` is what makes a fresh code go out when they arrive back here.
   */
  const handleChangeNumber = () => {
    const target = step?.verifies;
    const backTo = target
      ? steps.findIndex((s) => (s.fields ?? []).some((f) => f.id === target))
      : -1;
    clearOtp();
    setFormError(null);
    setOtpSentFor(null);
    setResendIn(0);
    setStepIndex(backTo >= 0 ? backTo : Math.max(0, stepIndex - 1));
  };

  /**
   * widget_impression — inline only.
   *
   * A popup fires `popup_open` from whatever opened it, so firing an impression here too
   * would double-count the same view. Skipped entirely in preview: an admin flipping
   * through skins is not an impression.
   */
  useEffect(() => {
    if (previewOnly || surface === "popup") return;
    return trackWidgetImpression(rootRef, { widget_name: FORM_NAME });
  }, [previewOnly, surface]);

  /**
   * Resolve states across the WHOLE journey, not just this step: a rule may depend on any
   * earlier step's answer (R21). The pruned `answers` it returns are authoritative — a
   * field hidden by a later edit must not keep the value the visitor typed before.
   */
  const states = useMemo(() => resolveFieldStates(steps, answers), [steps, answers]);

  const visibleFields = useMemo(
    () => (step && !isOtpStep ? visibleFieldsForStep(steps, step.id, answers) : []),
    [steps, step, isOtpStep, answers],
  );

  // ── Option sources ────────────────────────────────────────────────────────────────
  const [options, setOptions] = useState<Record<string, LeadFormOption[]>>({});
  const [optionsLoading, setOptionsLoading] = useState<Record<string, boolean>>({});

  /**
   * The course question pinned to the page's course — but ONLY when that course is one of
   * the choices the question actually offers.
   *
   * The skin asks for the field to be pinned (see `bindsCourseToPage`); whether it CAN be
   * is decided here, against the loaded options. A question bound to a narrowed collection
   * may legitimately not include this page's course, and pinning it there would either show
   * a disabled control reading "Select…" or submit a value the author never offered. When
   * the course is not on the list the field stays open and the visitor answers it, which is
   * the honest fallback — the page is the only thing that thinks it knows the answer.
   *
   * Null while the options are still loading, so nothing is pinned against an empty list;
   * it re-evaluates when they arrive.
   */
  const lockedCourse = useMemo(() => {
    const binding = session.courseBinding;
    if (!binding) return null;
    const field = steps
      .flatMap((step) => (Array.isArray(step.fields) ? step.fields : []))
      .find((f) => f?.id === binding.fieldId);
    if (!field) return null;
    const list = field.optionsSource
      ? (options[binding.fieldId] ?? [])
      : ((field.options as LeadFormOption[] | undefined) ?? []);
    return list.some((o) => String(o.value) === binding.value) ? binding : null;
  }, [session.courseBinding, steps, options]);

  /**
   * Hold the question at that course.
   *
   * Enforced against `answers` rather than painted on at render, because `answers` is what
   * gets validated and submitted — a value that existed only visually would send the CRM
   * whatever was underneath it. The control is disabled below, so this is the only writer,
   * and it re-asserts if a cookie seed or the store's prefill sets it back.
   */
  useEffect(() => {
    if (!lockedCourse) return;
    setAnswers((prev) =>
      prev[lockedCourse.fieldId] === lockedCourse.value
        ? prev
        : { ...prev, [lockedCourse.fieldId]: lockedCourse.value },
    );
  }, [lockedCourse]);

  /**
   * Fetch the live options for the fields on screen.
   *
   * Keyed by field id AND the value it depends on, so cities re-fetch when the state
   * changes but nothing else re-fetches when an unrelated answer does. Fields with a
   * static `options` array never reach the network.
   */
  useEffect(() => {
    const wanted = visibleFields.filter((f) => f.optionsSource && !isSpacerField(f));

    wanted.forEach((field) => {
      const dependsValue = field.dependsOn ? String(answers[field.dependsOn] ?? "") : "";
      // A dependent source with nothing chosen yet has no meaningful list to fetch.
      if (field.dependsOn && !dependsValue) {
        setOptions((prev) => (prev[field.id]?.length ? { ...prev, [field.id]: [] } : prev));
        return;
      }
      const cacheKey = `${field.id}::${field.optionsSource}::${field.optionsGroup ?? ""}::${dependsValue}`;
      if (loadedKeys.current.has(cacheKey)) return;
      loadedKeys.current.add(cacheKey);

      setOptionsLoading((prev) => ({ ...prev, [field.id]: true }));
      void resolveLeadFormOptions({
        source: String(field.optionsSource),
        universityId,
        group: field.optionsGroup,
        dependsOnValue: dependsValue || undefined,
      }).then((rows) => {
        // Guard on UNMOUNT, never on "this effect re-ran".
        //
        // A per-run `cancelled` flag looked equivalent and was not: React StrictMode runs
        // mount effects twice with a cleanup between them, and `loadedKeys` is a ref, so it
        // survives. Run 1 fired the request and was then cancelled; run 2 saw the key
        // already cached and fired nothing; run 1's response was discarded. Every
        // source-backed dropdown — courses, qualifications, states, cities — stayed
        // permanently empty, with no error anywhere to explain it.
        if (!mounted.current) return;
        setOptions((prev) => ({ ...prev, [field.id]: rows }));
        setOptionsLoading((prev) => ({ ...prev, [field.id]: false }));
      });
    });
  }, [visibleFields, answers, universityId]);

  // Resend cooldown, mirroring the 30s the legacy intake uses.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // ── Analytics helpers ─────────────────────────────────────────────────────────────

  /** Fired once per field, the first time the visitor focuses it. */
  const handleFillStart = useCallback(
    (field: LeadFormFieldSchema) => {
      if (previewOnly) return;
      if (fillStarted.current.has(field.id)) return;
      fillStarted.current.add(field.id);
      trackLeadformFillstart({
        form_name: FORM_NAME,
        // The catalog key is the stable analytics name; the field id is author-chosen and
        // would make two universities' funnels incomparable.
        field_name: field.catalog ?? field.apiKey ?? field.id,
      });
    },
    [previewOnly],
  );

  /** The five sheet-named field events, fired on a completed answer. */
  const trackFieldAnswered = useCallback(
    (field: LeadFormFieldSchema, value: unknown) => {
      if (previewOnly) return;
      const kind = field.catalog ? TRACKED_CATALOG_FIELDS[field.catalog] : undefined;
      if (!kind) return;
      const str = String(value ?? "").trim();
      if (!str) return;
      if (kind === "mobile") trackLeadformMobile({ form_name: FORM_NAME });
      else if (kind === "name") trackLeadName({ form_name: FORM_NAME });
      else if (kind === "email") trackLeadformEmail({ form_name: FORM_NAME });
      else if (kind === "hq") trackLeadHq({ form_name: FORM_NAME, selected_qualification: str });
      else if (kind === "aoi") trackLeadAoi({ form_name: FORM_NAME, selected_program: str });
    },
    [previewOnly],
  );

  const reportFieldErrors = useCallback(
    (errors: Record<string, string>) => {
      if (previewOnly) return;
      const byId = new Map(
        steps
          .flatMap((s) => (Array.isArray(s.fields) ? s.fields : []))
          .map((f) => [f.id, f] as const),
      );
      Object.entries(errors).forEach(([fieldId, message]) => {
        const f = byId.get(fieldId);
        trackLeadformError({
          form_name: FORM_NAME,
          field_name: f?.catalog ?? f?.apiKey ?? fieldId,
          error_message: String(message),
        });
      });
    },
    [previewOnly, steps],
  );

  // ── Interaction ───────────────────────────────────────────────────────────────────

  const setValue = useCallback((fieldId: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  /** Advance, skipping any step whose every field is currently hidden. */
  const advanceFrom = useCallback(
    (from: number, nextAnswers: Record<string, unknown>) => {
      for (let i = from + 1; i < steps.length; i += 1) {
        const s = steps[i];
        if (s?.kind === "otp") return i;
        const shown = visibleFieldsForStep(steps, s.id, nextAnswers).filter((f) => !isSpacerField(f));
        // A step reduced to nothing but spacers has nothing to ask — showing it would be a
        // dead screen with only a Continue button.
        if (shown.length > 0) return i;
      }
      return steps.length;
    },
    [steps],
  );

  /**
   * WHICH STEP DROPS THE LEAD.
   *
   * The author marks one with `submitsLead`; a schema with none resolves to the last step,
   * which is where submission has always happened — so an older form behaves identically.
   * See server/lib/leadFormRules.js for why this is a choice at all.
   */
  const submitStepIndex = useMemo(() => resolveSubmitStepIndex(steps), [steps]);

  /**
   * Guards the one-drop rule against a double-click or a re-render mid-request.
   *
   * Per-mount, which is exactly the right lifetime: a fresh journey is a fresh mount (the
   * admin preview remounts on its nonce key, a popup on each open), and within one journey
   * the lead must be delivered once no matter how the visitor navigates.
   */
  const submitted = useRef(false);

  /**
   * Deliver the lead to the CRM — the ONE drop.
   *
   * Separate from `finish` because the two no longer coincide: submission can happen at the
   * OTP gate with three enrichment steps still to come, so "the lead has been delivered" and
   * "the visitor has reached the end" are now different events. Collapsing them, as this
   * used to, would either drop the lead too late or end the journey too early.
   */
  const submitLead = useCallback(
    async (id: string) => {
      if (submitted.current) return true;
      submitted.current = true;
      if (previewOnly) return true;
      const res = await completeLeadForm({ leadId: id, configId, isPreview, mode: "submit" });
      if (!res.ok) {
        setFormError(res.message ?? "Could not finish. Please try again.");
        trackLeadSubmitFail({ form_name: FORM_NAME, reason: res.message ?? "complete_failed" });
        return false;
      }
      /**
       * The full success contract, matching what Leaddrop9 fires. These are NOT optional
       * extras: `pushToGTMHashed` feeds paid-media conversion matching, `authSuccess` is
       * what the legacy funnel dashboards read, and `identifyUser` is what stitches the
       * anonymous session to a person in CleverTap. A custom form that skipped them would
       * be invisible in exactly the reports the business uses to judge it.
       */
      const who = identityFromAnswers(steps, answers);
      pushToGTMHashed(who.email, who.phone, "lead_drop_form");

      const info = {
        university: process.env.NEXT_PUBLIC_UNIVERSITY_NAME,
        mobile: who.phone,
        name: who.name,
        email: who.email,
        highest_qualification: who.qualification,
        area_of_interest: who.interest,
      };
      authSuccess({
        section: "",
        component: "submit_button",
        label: "submit",
        userInfo: info,
        additionalInfo: info,
        isFirstByUser: true,
      });

      trackLeadSubmit({ form_name: FORM_NAME, lead_id: id });

      // Identify on whatever stable handle the author actually collected. Calling
      // identify with an empty id would collapse every visitor onto one profile, which
      // is worse than not identifying at all.
      const userKey = who.email || who.phone;
      if (userKey) {
        identifyUser(userKey, {
          name: who.name,
          email: who.email,
          phone: who.phone,
          qualification: who.qualification,
          lead_id: id,
        });
      }

      /**
       * Write the session back — the last thing, and only after a confirmed drop.
       *
       * Deliberately after `authSuccess`/`identifyUser` rather than before: `persist` sets
       * `lead_submitted_successfully`, which is what stops `useNonVerifiedCapture` from
       * re-arming the sweep. Setting it earlier would be harmless today but couples the
       * analytics contract to a flag that exists for the sweep, and the ordering is the
       * only thing keeping "this lead is finished" from racing "this lead is abandoned".
       */
      /**
       * The OMS session, exactly as lead form 8 stores it.
       *
       * Without this a visitor sent to `/application/{pok}` — which the skins can now do —
       * meets a login wall at the moment they converted. Both values are null when OMS is
       * unconfigured, the profile is incomplete, or this is a preview, and the cookie is
       * then simply not written.
       */
      if (res.token && res.cookieKey) {
        setCookie({
          name: res.cookieKey,
          value: res.token,
          maxAge: OMS_TOKEN_COOKIE_MAX_AGE_SEC,
        });
      }

      session.persist({
        leadId: res.leadsId ?? id,
        // Persisted so the thank-you CTA and a return visit still know where to send them,
        // the same way variant 8 keeps it on the userData cookie.
        applicationRedirectUrl: res.applicationRedirectUrl ?? undefined,
      });

      onComplete?.({ leadId: id, leadsId: res.leadsId ?? null });
      return true;
    },
    // `session.persist` is stable (it reads the live answers from a ref), so this chain is
    // not rebuilt on every keystroke — see useLeadFormSession.
    [previewOnly, configId, isPreview, onComplete, steps, answers, session.persist],
  );

  /**
   * Push a step answered AFTER submission into the CRM record that already exists.
   *
   * Fire-and-forget on purpose: the answers are already persisted on our own row by the
   * step submit that just succeeded, so a failed enrichment call costs a stale CRM field,
   * not a lost lead. Blocking the visitor's next screen on it would trade something that
   * matters for something that does not.
   */
  const syncLead = useCallback(
    (id: string) => {
      if (previewOnly || !id) return;
      void completeLeadForm({ leadId: id, configId, isPreview, mode: "sync" });
    },
    [previewOnly, configId, isPreview],
  );

  /**
   * Reaching the end of the journey. Submission has normally already happened at an earlier
   * step; `submitLead` is idempotent, so calling it here covers the ordinary case where the
   * submit step IS the last one.
   */
  const finish = useCallback(
    async (id: string) => {
      const ok = await submitLead(id);
      if (!ok) return false;
      setDone(true);
      return true;
    },
    [submitLead],
  );

  /**
   * What happens to the CRM when the steps in `[from, next)` have just been passed.
   *
   * A RANGE, not a single index, because advancing can jump: `advanceFrom` skips any step
   * that would render nothing, so the step the author marked as the submit step may be
   * stepped OVER rather than landed on. Keyed on one index, delivery then never fired and
   * the later steps' `sync` calls were no-ops (sync does nothing before the drop) — the
   * lead reached our own row and never reached the CRM at all.
   *
   * One place, so the call sites (submit, skip, OTP verify, auto-skip, and the end of the
   * journey) cannot drift on the rule that matters most: exactly one drop, at or after the
   * step the author chose, and never before the gate.
   */
  const deliverForPassedSteps = useCallback(
    async (from: number, next: number, id: string) => {
      if (!id) return;
      if (submitStepIndex >= from && submitStepIndex < next) await submitLead(id);
      else if (from > submitStepIndex) syncLead(id);
    },
    [submitStepIndex, submitLead, syncLead],
  );

  const handleSubmitStep = useCallback(async () => {
    if (!step) return;
    setBusy(true);
    setFormError(null);
    setFieldErrors({});

    // Send only THIS step's visible, answerable fields, read from the pruned answer set.
    // Posting a hidden field's stale value is exactly how a value the visitor never
    // intended reaches the CRM.
    const values: Record<string, unknown> = {};
    visibleFields.forEach((f) => {
      if (isSpacerField(f)) return;
      if (states.answers[f.id] !== undefined) values[f.id] = states.answers[f.id];
    });
    visibleFields.forEach((f) => trackFieldAnswered(f, values[f.id]));

    if (previewOnly) {
      // Walk the journey without a network call, so a skin can be reviewed end to end.
      const merged = { ...answers, ...values };
      setAnswers(merged);
      setBusy(false);
      const next = advanceFrom(stepIndex, merged);
      if (next >= steps.length) await finish("preview");
      else setStepIndex(next);
      return;
    }

    const res = await submitLeadFormStep({
      stepId: step.id,
      configId,
      values,
      leadId,
      captureSessionId: sessionIdRef.current,
      context: submitContext,
    });
    setBusy(false);

    if (!res.ok) {
      if (res.errors) {
        setFieldErrors(res.errors);
        reportFieldErrors(res.errors);
      } else {
        trackLeadSubmitFail({ form_name: FORM_NAME, reason: res.message ?? "step_rejected" });
      }
      if (res.otpRequired) {
        // The server refused because a gate is unsatisfied. Jump to it rather than
        // showing an error the visitor cannot act on.
        const gateIndex = steps.findIndex((s) => s.id === (res.otpRequired as OtpRequired).step_id);
        if (gateIndex >= 0) setStepIndex(gateIndex);
      }
      setFormError(res.errors ? null : (res.message ?? "Please check the form and try again."));
      return;
    }

    // The id always comes back — new on the identity step, an echo afterwards.
    if (res.lead_id) setLeadId(res.lead_id);
    const merged = { ...answers, ...values };
    setAnswers(merged);

    const id = res.lead_id || leadId || "";
    // Work out where we land FIRST, then deliver for everything passed on the way. Any step
    // between here and there renders nothing and is being skipped — including, possibly,
    // the submit step itself.
    const next = advanceFrom(stepIndex, merged);
    // Deliver BEFORE advancing: the drop belongs to the steps just passed, not to whatever
    // comes next, and a visitor who closes the tab on the following screen must still have
    // reached the CRM.
    await deliverForPassedSteps(stepIndex, next, id);

    if (next >= steps.length) {
      await finish(id);
      return;
    }
    setStepIndex(next);
  }, [
    step, visibleFields, states.answers, configId, leadId, submitContext, steps, answers, stepIndex,
    advanceFrom, finish, deliverForPassedSteps, previewOnly, trackFieldAnswered, reportFieldErrors,
  ]);

  /**
   * Skip — the brief's second CTA. Partial input is still captured, because everything
   * answered so far was already persisted by the steps that came before.
   */
  const handleSkip = useCallback(async () => {
    if (!previewOnly && !leadId) return;
    setBusy(true);
    setFormError(null);
    const id = leadId ?? "preview";
    // A skipped step is still a PASSED step. If the author put submission on it, skipping
    // must deliver the lead — otherwise the one optional screen in a journey silently
    // becomes the difference between a lead arriving and not.
    const next = advanceFrom(stepIndex, answers);
    await deliverForPassedSteps(stepIndex, next, id);

    if (next >= steps.length) await finish(id);
    else setStepIndex(next);
    setBusy(false);
  }, [previewOnly, leadId, stepIndex, answers, steps.length, advanceFrom, finish, deliverForPassedSteps]);

  const handleSendOtp = useCallback(async () => {
    if (!step) return;
    if (previewOnly) {
      setOtpSentFor(step.id);
      setResendIn(30);
      return;
    }
    if (!leadId) return;
    setBusy(true);
    setFormError(null);
    const res = await sendLeadFormOtp({ leadId, configId, stepId: step.id });
    setBusy(false);
    if (!res.ok) {
      setFormError(res.message ?? "Could not send the code. Please try again.");
      trackLeadSubmitFail({ form_name: FORM_NAME, reason: res.message ?? "otp_send_failed" });
      return;
    }
    setOtpSentFor(step.id);
    setResendIn(30);
  }, [previewOnly, leadId, step, configId]);

  // Issue the code as soon as the gate is reached — making the visitor press "send" first
  // is a step with no decision in it.
  useEffect(() => {
    if (!isOtpStep || busy) return;
    if (!previewOnly && !leadId) return;
    if (otpSentFor === step?.id) return;
    void handleSendOtp();
  }, [isOtpStep, previewOnly, leadId, otpSentFor, step?.id, busy, handleSendOtp]);

  const handleVerifyOtp = useCallback(async () => {
    if (!step) return;
    if (previewOnly) {
      clearOtp();
      const next = advanceFrom(stepIndex, answers);
      if (next >= steps.length) await finish("preview");
      else setStepIndex(next);
      return;
    }
    if (!leadId) return;
    setBusy(true);
    setFormError(null);
    const res = await verifyLeadFormOtp({ leadId, configId, stepId: step.id, otp });
    setBusy(false);
    if (!res.ok) {
      setFormError(res.message ?? "That code is not right — check it and try again.");
      trackLeadSubmitFail({ form_name: FORM_NAME, reason: res.message ?? "otp_verify_failed" });
      return;
    }
    clearOtp();
    // `firstVerification === false` means this mobile was ALREADY verified before — a
    // returning visitor, who must not be thanked for registering.
    if (res.firstVerification === false && outcome !== "download") setOutcome("existing");
    // The gate itself may be the submit step — "deliver the moment the number is proven",
    // which is the whole reason submission became a choice.
    const next = advanceFrom(stepIndex, answers);
    await deliverForPassedSteps(stepIndex, next, leadId);

    if (next >= steps.length) await finish(leadId);
    else setStepIndex(next);
  }, [previewOnly, leadId, step, configId, otp, stepIndex, answers, steps.length, advanceFrom, finish, deliverForPassedSteps, outcome, clearOtp]);

  /**
   * A step that renders nothing is never shown.
   *
   * `advanceFrom` already skips such steps when moving FORWARD, but two cases slipped
   * through and both ended the same way — a dead screen whose Continue button is disabled
   * (`answerableCount === 0`), with no way onward:
   *
   *   · the FIRST step, which nothing advances into, so it was never tested
   *   · a step that becomes empty AFTER it is on screen, when a conditional rule hides its
   *     last remaining field
   *
   * Landing past the end finishes the journey, so an emptied tail goes straight to the
   * thank-you / returning / brochure screen rather than stranding the visitor one click
   * short of it.
   *
   * Latched per step index: a failed drop must not put this into a retry loop, and
   * `advanceFrom` only ever moves forward, so the latch cannot hide a step that should
   * have been shown.
   */
  const autoSkipped = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (previewOnly || done || busy) return;
    if (!step || isOtpStep) return;
    if (visibleFields.some((f) => !isSpacerField(f))) return;
    if (autoSkipped.current.has(stepIndex)) return;
    autoSkipped.current.add(stepIndex);

    void (async () => {
      const next = advanceFrom(stepIndex, answers);
      const id = leadId ?? "";
      // The submit step may be among the ones being stepped over.
      await deliverForPassedSteps(stepIndex, next, id);
      if (next < steps.length) {
        setStepIndex(next);
        return;
      }
      // Nothing left to ask. Without a lead there is nothing to finish either — a form
      // that collected no answer at all must not show a thank-you for a lead that was
      // never captured.
      if (id) await finish(id);
    })();
  }, [
    previewOnly, done, busy, step, isOtpStep, visibleFields, stepIndex, answers, leadId,
    steps.length, advanceFrom, deliverForPassedSteps, finish,
  ]);

  // ── Render ────────────────────────────────────────────────────────────────────────

  /**
   * Which brochure the download flow actually delivers.
   *
   * The course the visitor PICKED decides it — not the CTA that opened the form. This is
   * what makes a "Download Brochure" button work on a page with no course of its own
   * (a generic landing page, Banner 10): the trigger has no link to pass, and the course
   * chosen inside the form supplies one. `lib/brochure.ts` states the same rule for the
   * hand-built variants; the difference here is that a config form has no fixed field
   * named "course", so the course field is found by its SOURCE.
   *
   * The CTA's link stays as the fallback, for a form with no course question at all.
   */
  const selectedBrochure = useMemo(() => {
    // Author order, not `Object.keys(options)` order — the latter is insertion order of
    // whichever source resolved first, so a form with two course questions would pick a
    // brochure that depends on network timing.
    for (const field of steps.flatMap((st) => st.fields ?? [])) {
      const picked = String(answers[field.id] ?? "").trim();
      if (!picked) continue;
      const match = (options[field.id] ?? []).find((o) => o.value === picked);
      const brochure = String(match?.brochure ?? "").trim();
      if (brochure) return brochure;
    }
    return "";
  }, [steps, options, answers]);
  const resolvedDownloadLink = selectedBrochure || downloadLink;

  /**
   * The POK of the course the visitor actually picked.
   *
   * Same shape as `selectedBrochure`, and the same reason for iterating in AUTHOR order
   * rather than over `Object.keys(options)`: the latter is insertion order of whichever
   * source resolved first, so a form with two course questions would resolve against
   * whichever request happened to land first.
   */
  /**
   * The programme the visitor chose, as its LABEL.
   *
   * The stored answer is the option's VALUE (an erp_unicode like "DS-01"), which is what the
   * CRM wants and what nobody should be shown. The label is resolved from the same loaded
   * options the dropdown rendered, falling back to the raw answer so a form whose options
   * have not loaded still says something rather than nothing.
   */
  const courseLabel = useMemo(() => {
    const ids = steps.flatMap((st) => st.fields ?? []);
    for (const field of ids) {
      if (field.catalog !== "area_of_interest") continue;
      const picked = String(answers[field.id] ?? "").trim();
      if (!picked) continue;
      const match = (options[field.id] ?? []).find((o) => o.value === picked);
      return String(match?.label ?? picked).trim();
    }
    return "";
  }, [steps, options, answers]);

  const selectedPok = useMemo(() => {
    for (const field of steps.flatMap((st) => st.fields ?? [])) {
      const picked = String(answers[field.id] ?? "").trim();
      if (!picked) continue;
      const match = (options[field.id] ?? []).find((o) => o.value === picked);
      const pok = String(match?.pok ?? "").trim();
      if (pok) return pok;
    }
    return "";
  }, [steps, options, answers]);

  /**
   * Where the success button goes.
   *
   * A skin may point it at the application page for the course the visitor chose, rather
   * than at one fixed destination — `{slug}` in the path is that course's POK.
   *
   * Every fallback here is deliberate. The redirect needs THREE things to be true: the
   * author enabled it, they gave a path, and the picked course actually has a POK. Any one
   * missing and the configured `success_button_link` is used instead — a button that
   * silently pointed at `/application/` with nothing after it would send the visitor to a
   * broken page at the exact moment they converted.
   *
   * Resolution lives here, not in the skin: a skin decides WHETHER to offer this, but every
   * skin that offers it wants the same substitution. See skins/types.ts on that split.
   */
  const successButtonLink = useMemo(() => {
    const configured = String(v.success_button_link ?? "").trim();
    if (v.enable_application_redirect !== true) return configured;
    const template = String(v.application_redirect_path ?? "").trim();
    /**
     * In PREVIEW, stand in a sample POK when no course has been chosen.
     *
     * The redirect resolves from the course the visitor picked, and nothing is picked while
     * an author clicks through their own draft — so the link came back empty, the CTA was
     * hidden, and the author could not see or check the button they had just configured.
     * The link is inert in preview anyway (`handleBack` returns early), so standing one in
     * shows the real layout without being able to navigate anywhere.
     */
    const pok = selectedPok || (previewOnly ? "PREVIEW" : "");
    if (!template || !pok) return configured;
    // Replace every occurrence, so a template that repeats the token is not half-resolved.
    return template.split("{slug}").join(encodeURIComponent(pok));
  }, [
    v.success_button_link, v.enable_application_redirect, v.application_redirect_path,
    selectedPok, previewOnly,
  ]);

  const cssVars = skin.cssVars(v);
  if (steps.length === 0) {
    // An empty journey is an authoring state, not an error the visitor should read about.
    // In preview it must be said out loud, or the admin sees a blank box and no reason.
    return previewOnly ? (
      <LeadFormShell
        skin={skin}
        values={v}
        surface={surface}
        className={className}
        style={cssVars}
        rootRef={rootRef}
        onClose={onClose}
      >
        <p className={c.subtitle}>
          This form has no steps yet. Add fields in the Form Builder to see them here.
        </p>
      </LeadFormShell>
    ) : null;
  }

  if (done) {
    /**
     * WHERE the ending lives is a per-design choice.
     *
     * Lead form 9's is its own card — different padding, a 560px floor, and a decoration
     * that bleeds past the top edge — so nesting it inside the form card produced a card
     * inside a card with the illustration clipped twice. Variant 8's is the opposite: the
     * dialog keeps its hero rail and its sheet, and only the column beside the image
     * changes, so replacing the shell threw away the image the design is built around.
     *
     * `endingKeepsShellChrome` is that choice, declared by the skin. It applies to BOTH
     * surfaces: variant 8's inline card is kept on the thank-you exactly as its dialog is.
     */
    /**
     * Both surfaces, not just the popup: variant 8's inline form keeps its lavender card on
     * the thank-you exactly as the dialog keeps its sheet. Replacing the card inline was
     * why the ending appeared as a bare block where a lavender panel had been.
     */
    const keepsShellChrome = skin.endingKeepsShellChrome === true;

    const ending = (
      <TerminalScreen
        skin={skin}
        values={v}
        outcome={outcome}
        downloadLink={resolvedDownloadLink}
        successButtonLink={successButtonLink}
        courseLabel={courseLabel}
        previewOnly={previewOnly}
        surface={surface}
        bare={keepsShellChrome}
        className={className}
        style={cssVars}
        rootRef={rootRef}
        onClose={onClose}
      />
    );

    if (keepsShellChrome) {
      return (
        <LeadFormShell
          skin={skin}
          values={v}
          surface={surface}
          className={className}
          style={cssVars}
          rootRef={rootRef}
          onClose={onClose}
        >
          {ending}
        </LeadFormShell>
      );
    }
    return ending;
  }


  const answerableCount = visibleFields.filter((f) => !isSpacerField(f)).length;
  const fieldSteps = steps.filter((s) => s.kind !== "otp");
  const stepNumber = fieldSteps.findIndex((s) => s.id === step?.id) + 1;
  const isLastStep = stepIndex === steps.length - 1;
  const showStepMeta = v.show_step_indicator !== false && fieldSteps.length > 1 && !isOtpStep;

  // A step's own title beats the form-level one: a multi-step journey where every screen
  // says the same thing tells the visitor nothing about where they are.
  // The header stays put across steps, gate included — lead form 9 keeps its title and
  // sub text above the OTP boxes, and swapping it for a second heading made the gate read
  // as a different form rather than the next screen of this one.
  /**
   * Per-step overrides beat everything.
   *
   * The precedence is deliberate and runs narrowest-first: what the author wrote for THIS
   * step, then the step's own title from the Form Builder, then the form-level setting.
   * Each rung is only reached when the one above it is blank, so a form-level change still
   * moves every screen that has not been individually overridden — which is what makes the
   * global settings worth having at all.
   */
  const stepOverrides = resolveStepOverrides(v, step);
  const title = stepOverrides.title || step?.title || v.form_title;
  const subtitle = stepOverrides.subtitle ?? v.form_sub_text;

  /**
   * A step may repaint the CTA; falling through to the skin's variables when it does not.
   *
   * Withheld entirely while the button is disabled: an inline background beats any class,
   * so painting here would defeat whatever `buttonDisabled` does — and a skin whose
   * disabled state is a different FILL rather than reduced opacity (variant 8's
   * `get_otp_disabled_color`) would show an enabled-looking button that does nothing.
   */
  const ctaStyle = (disabled: boolean, otpStep: boolean) =>
    disabled
      ? undefined
      : {
          backgroundColor:
            stepOverrides.buttonColor ?? `var(--lf-primary${otpStep ? "-2" : ""})`,
          color:
            stepOverrides.buttonTextColor ?? `var(--lf-on-primary${otpStep ? "-2" : ""})`,
        };

  /**
   * Whether this screen's heading sits ON the image strip — see `headerOnAside`.
   *
   * Two conditions, and both matter. The skin has to want it (skin 3000's title belongs
   * beside its rail, not on it), and an image has to actually be configured: the class
   * takes the header out of flow, so with no strip to sit on it would land across the
   * first field. Same test `LeadFormShell` uses to decide whether to draw the aside at
   * all, so the two cannot disagree.
   */
  const headerOnAside =
    surface === "popup" &&
    Boolean(skin.classes.headerOnAside) &&
    Boolean(v.popup_desktop_image || v.popup_mobile_image);

  return (
    <LeadFormShell
      skin={skin}
      values={v}
      surface={surface}
      className={className}
      style={cssVars}
      rootRef={rootRef}
      onClose={onClose}
    >
      {(title || subtitle) && (
        <div className={cn(c.header, headerOnAside && c.headerOnAside)}>
          {title && <p className={c.title}>{title}</p>}
          {subtitle && <p className={c.subtitle}>{subtitle}</p>}
          {showStepMeta && (
            <p className={c.stepMeta}>{`Step ${stepNumber} of ${fieldSteps.length}`}</p>
          )}
        </div>
      )}

      {formError && !isOtpStep && <div className={c.banner}>{formError}</div>}

      {isOtpStep ? (
        /* The gate is a FORM like every other step, not a detached pane: same footer,
           same consent line, and Enter submits. Lead form 9 shares one footer across its
           two screens for the same reason — a visitor should not see the CTA move. */
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleVerifyOtp();
          }}
          className="flex flex-1 flex-col gap-[16px]"
          noValidate
        >
          <div className={c.otpBlock}>
            <p className={c.otpDescription}>
              {`${v.otp_description_text ?? ""} ${maskedPhone}. `}
              <button
                type="button"
                onClick={handleChangeNumber}
                className={c.otpInlineAction}
                style={{ color: "var(--lf-edit-number)" }}
              >
                {v.edit_number_text}
              </button>
            </p>

            <p className={c.otpHeader}>{v.otp_header_text}</p>

            {/* One box per digit. A single wide field with letter-spacing looks close at a
                glance and behaves nothing like it: no auto-advance, no backspace-to-previous,
                and a paste lands in one box. */}
            <div className={c.otpBoxes}>
              {Array.from({ length: OTP_LENGTH }, (_, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpBoxRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  // Only the first box advertises the SMS code, or the browser offers to
                  // autofill all four with the same digit.
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={otpDigits[index] ?? ""}
                  onChange={(e) => handleOtpDigit(index, e.target.value)}
                  onPaste={handleOtpPaste}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
                      otpBoxRefs.current[index - 1]?.focus();
                    }
                  }}
                  disabled={busy}
                  aria-label={`OTP digit ${index + 1}`}
                  aria-invalid={formError ? true : undefined}
                  className={cn(c.otpBox, formError && c.otpBoxInvalid)}
                />
              ))}
            </div>

            {formError && <p className={c.errorText}>{formError}</p>}

            <p className={c.otpResend}>
              {`${v.resend_prefix_text ?? ""} `}
              {resendIn > 0 ? (
                <span className="font-semibold" style={{ color: "var(--lf-resend)" }}>
                  {v.resend_otp_text} in {resendIn} sec
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleSendOtp()}
                  disabled={busy}
                  className={c.otpInlineAction}
                  style={{ color: "var(--lf-resend)" }}
                >
                  {v.resend_otp_text}
                </button>
              )}
            </p>
          </div>

          <div className={c.actions}>
            <button
              type="submit"
              disabled={busy || otp.length < OTP_LENGTH}
              className={cn(c.button, (busy || otp.length < OTP_LENGTH) && c.buttonDisabled)}
              // The OTP step has its own colour knobs, so it can be visually distinguished
              // from the collect steps the way lead form 9 distinguishes them.
              style={ctaStyle(busy || otp.length < OTP_LENGTH, true)}
            >
              {busy ? "Verifying…" : (stepOverrides.buttonText ?? v.button_step_2_text)}
            </button>
            {v.checkbox_description && (
              <p className={c.consent}>
                {renderConsentText(
                  v.checkbox_description,
                  [v.terms_conditions_link ?? "", v.privacy_policy_link ?? ""],
                  { linkClassName: c.consentLink, linkColor: v.checkbox_color },
                )}
              </p>
            )}
          </div>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmitStep();
          }}
          className="flex flex-1 flex-col gap-[16px]"
          noValidate
        >
          <div className={c.body}>
            {visibleFields.map((field) =>
              isSpacerField(field) ? (
                <div
                  key={field.id}
                  aria-hidden
                  className={colSpanClass(field.colSpan)}
                  style={{ height: spacerHeight(field.spacer?.height) }}
                />
              ) : (
                <div key={field.id} className={colSpanClass(field.colSpan)}>
                  <FieldControl
                    skin={skin}
                    field={field}
                    value={answers[field.id]}
                    required={states.required.has(field.id)}
                    disabled={
                      states.disabled.has(field.id) ||
                      field.id === lockedCourse?.fieldId ||
                      busy
                    }
                    error={fieldErrors[field.id]}
                    options={
                      field.optionsSource ? (options[field.id] ?? []) : (field.options ?? [])
                    }
                    optionsLoading={optionsLoading[field.id] === true}
                    onFocus={() => handleFillStart(field)}
                    onChange={(val) => setValue(field.id, val)}
                  />
                </div>
              ),
            )}
          </div>

          {/* Preview only. Live, this step is skipped before anyone sees it — but an author
              clicking through their own draft needs to be told that, or a step that
              vanishes at runtime looks like a bug rather than the rule they wrote. */}
          {previewOnly && answerableCount === 0 && (
            <p className={c.helpText}>
              Nothing to show on this step right now — every field is hidden by a condition,
              or the step has none. Visitors skip straight past it.
            </p>
          )}

          <div className={c.actions}>
            <button
              type="submit"
              disabled={busy || answerableCount === 0}
              className={cn(c.button, (busy || answerableCount === 0) && c.buttonDisabled)}
              style={ctaStyle(busy || answerableCount === 0, false)}
            >
              {busy
                ? "Please wait…"
                : (stepOverrides.buttonText ??
                  (isLastStep ? v.submit_text : v.button_step_1_text))}
            </button>
            {/* Skip only exists once a lead row is in play — before that there is nothing
                captured to skip forward from. */}
            {step?.optional === true && (previewOnly || leadId) && (
              <button
                type="button"
                onClick={() => void handleSkip()}
                disabled={busy}
                className={c.secondaryButton}
              >
                {v.skip_text}
              </button>
            )}
            {v.checkbox_description && (
              <p className={c.consent}>
                {renderConsentText(
                  v.checkbox_description,
                  [v.terms_conditions_link ?? "", v.privacy_policy_link ?? ""],
                  { linkClassName: c.consentLink, linkColor: v.checkbox_color },
                )}
              </p>
            )}
          </div>
        </form>
      )}
    </LeadFormShell>
  );
};

/**
 * The screen after the journey ends.
 *
 * Three endings, one component: the only differences are which settings it reads and
 * whether it starts a file. Branching into three components would have meant three places
 * to keep the layout in step.
 *
 * ─── It is a CARD, not a panel inside one ───────────────────────────────────────────
 * This renders the whole surface — background, padding, height floor, close button — the
 * way `LastCard` does for lead form 9. It is deliberately not wrapped in the form's shell:
 * the ending has its own padding and its own 560px floor, and the skin's backdrop is
 * anchored ABOVE the card and clipped by it, which only works if this element is the one
 * doing the clipping.
 */
/**
 * Wrap the body in the right surface tree.
 *
 * Embedded is one column in a bordered card. Popup is a wider shell that can carry a
 * full-height image rail beside the fields, plus a close affordance. They share nothing
 * but the content — see LeadFormSkinClasses on why this is not a width switch.
 *
 * ─── Why this lives at MODULE level ─────────────────────────────────────────────────
 * It was previously declared inside ConfigLeadForm, which made it a NEW function identity
 * on every render. React compares element types by reference, so each keystroke produced a
 * "different" component here, unmounted this entire subtree and mounted a fresh one — which
 * destroys the focused <input> and drops the caret. The symptom was that typing a single
 * character blurred the field, on every form, inline and popup alike.
 *
 * Any component that wraps stateful children MUST be declared outside the component that
 * renders it. Defining one inline is not a style preference; it silently remounts the tree.
 */
const LeadFormShell = ({
  skin,
  values,
  surface,
  className,
  style,
  rootRef,
  onClose,
  children,
}: {
  skin: LeadFormSkinDefinition;
  values: LeadFormSkinValues;
  surface: "fixed" | "popup";
  className?: string;
  style?: React.CSSProperties;
  rootRef: React.RefObject<HTMLDivElement | null>;
  onClose?: () => void;
  children: React.ReactNode;
}) => {
  const c = skin.classes;
  /**
   * Two art directions for one element. The rail beside the fields is tall and narrow
   * (394×565 on variant 8); the strip above them on a phone is short and wide (134px).
   * The same file cannot serve both — cropping a portrait rail to a 134px band shows a
   * sliver of it — so the author uploads two and the browser picks.
   *
   * Either one ALONE opens the rail, and stands in for the other: a form with only a
   * desktop image still needs something across the top of the phone sheet, and half a
   * design is better than a blank band mid-enquiry.
   */
  const desktopImage = String(values.popup_desktop_image ?? "").trim();
  const mobileImage = String(values.popup_mobile_image ?? "").trim();
  const popupImage = desktopImage || mobileImage;
  const asideDesktopFrom = skin.popupAsideDesktopFrom ?? 1080;

  if (surface !== "popup") {
    return (
      <div ref={rootRef} className={cn(c.card, className)} style={style}>
        <div className={c.cardInner}>{children}</div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn(c.popupShell, popupImage && c.popupShellWithAside, className)}
      style={style}
    >
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Close" className={c.popupClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.55)" />
            <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      )}
      <div className={c.popupInner}>
        {popupImage && (
          <div className={c.popupAside}>
            {/* `<picture>` rather than a width read in an effect: the swap has to survive
                server rendering, and anything derived from `window` on the first paint
                makes the markup disagree with the client's and throws the tree away. The
                browser resolves this before the request goes out, so only ONE file is
                fetched. `contents` keeps the <img> a direct child of the aside for
                layout — `h-full` would otherwise resolve against the inline <picture>. */}
            <picture className="contents">
              {/* Only when the two genuinely differ. A lone <source> pointing at the same
                  file as the fallback is a second candidate for no reason. */}
              {desktopImage && mobileImage && desktopImage !== mobileImage && (
                <source media={`(min-width: ${asideDesktopFrom}px)`} srcSet={desktopImage} />
              )}
              {/* Deliberately a plain <img>: this was `next/image` with `unoptimized`,
                  which is the same request with extra attributes, and next/image cannot
                  carry a <source> sibling. The rail is decoration beside the form — a
                  broken URL must fail to paint, never blank the whole dialog. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={mobileImage || desktopImage}
                alt={String(values.popup_image_alt || "")}
                className={c.popupAsideImage}
              />
            </picture>
          </div>
        )}
        <div className={c.popupColumn}>{children}</div>
      </div>
    </div>
  );
};

/**
 * Substitute `{course}` in an ending's copy with the programme the visitor chose.
 *
 * Returns NODES, not a string, because the course is styled differently from the sentence
 * around it — variant 8's thank-you sets the programme in its accent colour. A plain
 * `.replace()` could not express that, and asking authors to write two separate settings
 * for one sentence puts the word order in the wrong hands: "interest in {course}" and
 * "{course} — thank you" are both legitimate, and only the author knows which they want.
 *
 * With no course collected the token is dropped along with any doubled spaces it leaves,
 * so the sentence still reads as a sentence rather than showing a raw placeholder.
 */
const renderCourseText = (
  text: string | undefined,
  course: string,
  courseColor: string | undefined,
): React.ReactNode => {
  const raw = String(text ?? "");
  if (!raw) return null;
  if (!raw.includes("{course}")) return raw;
  const parts = raw.split("{course}");
  if (!course) return parts.join("").replace(/\s{2,}/g, " ").trim();
  const out: React.ReactNode[] = [];
  parts.forEach((part, i) => {
    if (part) out.push(part);
    if (i < parts.length - 1) {
      out.push(
        <span key={`c${i}`} style={courseColor ? { color: courseColor } : undefined}>
          {course}
        </span>,
      );
    }
  });
  return out;
};

const TerminalScreen = ({
  skin,
  values,
  outcome,
  downloadLink,
  successButtonLink,
  courseLabel,
  previewOnly,
  bare = false,
  surface,
  className,
  style,
  rootRef,
  onClose,
}: {
  skin: LeadFormSkinDefinition;
  values: LeadFormSkinValues;
  outcome: LeadFormOutcome;
  downloadLink?: string;
  /** Where the ending CTA goes, already resolved — see `successButtonLink` in the renderer. */
  successButtonLink: string;
  /** The programme the visitor chose, for `{course}` in the ending copy. */
  courseLabel: string;
  /**
   * Rendered INSIDE the popup shell, which already draws the card and the close button.
   * The ending then contributes only its content — see `endingKeepsShellChrome`.
   */
  bare?: boolean;
  previewOnly?: boolean;
  surface: "fixed" | "popup";
  className?: string;
  style?: React.CSSProperties;
  rootRef: React.RefObject<HTMLDivElement | null>;
  onClose?: () => void;
}) => {
  const c = skin.classes;
  const isPopup = surface === "popup";

  /**
   * Every ending carries its OWN colours, not one shared palette.
   *
   * "Thank you" and "welcome back" are different moments and authors colour them
   * differently; the legacy card reads a separate set of columns per state for exactly
   * this reason, and a shared `--lf-title` would have quietly flattened all three.
   */
  const content =
    outcome === "download"
      ? {
          title: values.download_title,
          titleColor: values.download_title_color,
          subtitle: values.download_subtitle,
          subtitleColor: values.download_subtitle_color,
          description: values.download_description,
          descriptionColor: values.download_description_color,
          image: values.download_background_image,
          /**
           * A CTA only when the SKIN asks for one.
           *
           * Some designs treat the file as the whole outcome and end there; variant 8's
           * brochure screen is its ordinary thank-you, button included. Reading a
           * download-specific key — rather than falling back to the new-user one — is what
           * lets each skin say which it is, without a change here altering the other.
           */
          buttonText: values.download_button_text ?? "",
          buttonColor: values.download_button_text_color ?? "",
        }
      : outcome === "existing"
        ? {
            title: values.existing_user_title,
            titleColor: values.existing_user_title_color,
            subtitle: values.existing_user_subtitle,
            subtitleColor: values.existing_user_subtitle_color,
            description: values.existing_user_description,
            descriptionColor: values.existing_user_description_color,
            image: values.existing_user_background_image,
            buttonText: values.existing_user_button_text,
            buttonColor: values.existing_user_button_text_color,
          }
        : {
            title: values.new_user_title,
            titleColor: values.new_user_title_color,
            subtitle: values.new_user_subtitle,
            subtitleColor: values.new_user_subtitle_color,
            description: values.new_user_description,
            descriptionColor: values.new_user_description_color,
            image: values.new_user_background_image,
            buttonText: values.new_user_button_text,
            buttonColor: values.new_user_button_text_color,
          };

  /**
   * Start the brochure automatically, exactly once.
   *
   * Suppressed in preview: an author flipping to the download screen to write its copy
   * must not have a file land in their downloads folder every time.
   */
  const started = useRef(false);
  useEffect(() => {
    if (previewOnly || outcome !== "download" || !downloadLink || started.current) return;
    started.current = true;
    void handleDownload(downloadLink, "brochure.pdf");
  }, [previewOnly, outcome, downloadLink]);

  /**
   * Auto-redirect: count down, then follow the success destination.
   *
   * Three guards, each protecting something different. No destination → nothing to go to,
   * so a countdown would strand the visitor on a screen that then did nothing. `previewOnly`
   * → an author reviewing a draft must not be navigated away mid-edit. A non-positive or
   * unparseable value → the author has turned it off, which is the default.
   *
   * The interval is cleared on unmount, so closing the popup mid-count cannot navigate the
   * page out from under someone who deliberately dismissed it.
   */
  const redirectSeconds = Math.floor(Number(values.success_redirect_seconds ?? 0));
  const hasDestination = Boolean(String(successButtonLink ?? "").trim());
  const countdownConfigured =
    hasDestination && Number.isFinite(redirectSeconds) && redirectSeconds > 0;
  /** Shown in preview so the author can see the line; only TICKS on a real ending. */
  const countdownRuns = !previewOnly && countdownConfigured;
  const [secondsLeft, setSecondsLeft] = useState(redirectSeconds);

  useEffect(() => {
    if (!countdownRuns) return;
    setSecondsLeft(redirectSeconds);
    // The remaining count is held in a plain closure variable, not read back out of state.
    // Deciding "are we at zero?" inside a `setSecondsLeft` updater put the navigation
    // inside a function React is allowed to call twice (it does, in StrictMode) — so the
    // redirect could fire twice, and an updater that navigates is not a pure updater.
    let left = redirectSeconds;
    const timer = setInterval(() => {
      left -= 1;
      setSecondsLeft(left > 0 ? left : 0);
      if (left <= 0) {
        clearInterval(timer);
        goToDestination(true);
      }
    }, 1000);
    return () => clearInterval(timer);
    // `goToDestination` is stable for the life of this screen — the ending does not
    // re-resolve its destination once shown, and re-running the timer on every render
    // would reset it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownRuns, redirectSeconds]);

  const triggerDownload = () => {
    if (!downloadLink) return;
    void handleDownload(downloadLink, "brochure.pdf");
  };

  /**
   * `{{ … }}` in the download message becomes the MANUAL download link.
   *
   * The wrapped text is always styled as a link, even with no brochure configured — the
   * legacy card does the same, because a sentence that reads "you can click here" with
   * nothing looking clickable reads as broken.
   */
  const renderDescription = (text: string | undefined) => {
    if (!text) return null;
    // `{course}` works in every ending; `{{ … }}` is download-only. Applied first so a
    // description can use both in one sentence.
    if (outcome !== "download" || !text.includes("{{")) {
      return renderCourseText(text, courseLabel, values.course_text_color);
    }
    const nodes: React.ReactNode[] = [];
    const re = /\{\{([^{}]+)\}\}/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let key = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) {
        nodes.push(renderCourseText(text.slice(last, m.index), courseLabel, values.course_text_color));
      }
      nodes.push(
        <span
          key={key++}
          role="button"
          tabIndex={0}
          onClick={triggerDownload}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              triggerDownload();
            }
          }}
          className="cursor-pointer font-semibold underline transition-opacity hover:opacity-80"
          style={{ color: values.brochure_link_color }}
        >
          {m[1].trim()}
        </span>,
      );
      last = re.lastIndex;
    }
    if (last < text.length) {
      nodes.push(renderCourseText(text.slice(last), courseLabel, values.course_text_color));
    }
    return nodes;
  };

  /**
   * The CTA, in the legacy card's order of preference: an explicit link wins and opens in
   * a new tab, otherwise close the popup, otherwise go home. An embedded form has nothing
   * to close, which is why the homepage is the last resort rather than the first.
   */
  const goToDestination = (auto: boolean) => {
    if (previewOnly) return;
    // The RESOLVED link, not the raw setting: a skin may point this at the application page
    // for the course the visitor picked.
    const link = String(successButtonLink ?? "").trim();
    if (link) {
      // The countdown navigates THIS tab; a click opens a new one.
      //
      // Not a preference — `window.open` only works while the browser has a user
      // activation to spend, and a `setInterval` callback has none, so every popup
      // blocker dropped it without a word: the timer reached zero and the visitor sat on
      // an ending screen that had promised to move. A redirect nobody asked for also
      // belongs in the tab they are already looking at, whereas a visitor who CHOSE the
      // button may still want the form behind it.
      if (auto) window.location.assign(link);
      else window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    if (onClose) return onClose();
    window.location.href = window.location.origin;
  };

  const handleBack = () => goToDestination(false);

  return (
    <div
      ref={bare ? undefined : rootRef}
      // Bare: the shell owns the card, the width and the scroll. Re-applying `successCard`
      // here would nest a second card inside the sheet — the exact bug the non-bare comment
      // above describes, only inverted.
      className={bare ? c.successContentBare : cn(c.successCard, isPopup && c.successCardPopup, className)}
      style={bare ? undefined : style}
    >
      {skin.renderBackdrop?.(values)}

      {/* Popup only — and never when the shell already drew one. */}
      {isPopup && !bare && onClose && (
        <button type="button" onClick={onClose} aria-label="Close" className={c.popupClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.55)" />
            <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {/*
        Not in BARE mode. The shell is still on screen there, hero rail and all, so the
        ending's own illustration is a SECOND copy of the imagery beside the first — which
        is exactly what it looked like. Variant 8's thank-you has no image element at all;
        its picture is the rail.
      */}
      {!bare && content.image && (
        <div className={c.successImage}>
          <Image
            src={content.image}
            alt=""
            fill
            className="object-contain object-bottom"
            // Decoration on a terminal screen: a broken URL must not blank the card.
            unoptimized
          />
        </div>
      )}

      <div className={c.successContent}>
        <div className={c.successHeadings}>
          {content.title && (
            <p className={c.successTitle} style={{ color: content.titleColor }}>
              {renderCourseText(content.title, courseLabel, values.course_text_color)}
            </p>
          )}
          {content.subtitle && (
            <p className={c.successSubtitle} style={{ color: content.subtitleColor }}>
              {renderCourseText(content.subtitle, courseLabel, values.course_text_color)}
            </p>
          )}
        </div>

        {content.description && (
          <p className={c.successBody} style={{ color: content.descriptionColor }}>
            {renderDescription(content.description)}
          </p>
        )}

        {/* "Back to Website" is popup-first: a dialog has something to close, an embedded
            form does not. It still appears inline once an author gives it a destination —
            that is a real place to send someone, not a no-op. */}
        {Boolean(content.buttonText) &&
          (isPopup || Boolean(String(successButtonLink ?? "").trim())) && (
          <button
            type="button"
            onClick={handleBack}
            className={c.successButton}
            /**
             * The outcome's colour is published as a VARIABLE, not applied directly.
             *
             * It used to set `borderColor` and `color` here, which silently decided that
             * every skin's ending CTA is an outlined pill whose text is that colour — a
             * skin wanting a FILLED button could not have one, because an inline `color`
             * beats any class it might add. Handing over the value and letting the class
             * bundle decide is what makes both shapes expressible.
             */
            style={{ "--lf-success-cta": content.buttonColor } as React.CSSProperties}
          >
            <span>{content.buttonText}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M14 4h6v6M20 4l-9 9M9 5H5a1 1 0 00-1 1v13a1 1 0 001 1h13a1 1 0 001-1v-4"
                // Follows the label, so it is legible on an outlined pill and on a filled one.
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {/* Only while the countdown is actually running — a static "Redirecting in 0 Sec"
            under a manual button would promise something that never happens. */}
        {countdownConfigured && (
          <p className={c.successCountdown}>
            Redirecting in <b>{Math.max(0, secondsLeft)} Sec</b>
          </p>
        )}
      </div>
    </div>
  );
};


/** One input. Kept dumb — every decision about it was already made by the evaluator. */
/**
 * Reduce anything a visitor can type or paste into a bare 10-digit Indian mobile.
 *
 * Truncating to the first 10 characters is not enough: people paste "+91 98765 43210" and
 * "091-98765-43210", and a naive cut keeps the country code and throws away the last digits
 * — turning a valid number into a plausible-looking wrong one, which is worse than rejecting
 * it. So the country/trunk prefix is removed BEFORE the cut.
 *
 * Mirrors server/utils/phone.js INDIAN_PHONE_REGEX (10 digits, starting 6-9). The server
 * still rejects anything that slips past; this exists so a legitimate paste is accepted
 * rather than bounced back at the visitor.
 */
function normalizeIndianMobile(raw: string): string {
  let digits = String(raw ?? "").replace(/\D/g, "");
  // "+91XXXXXXXXXX" / "0091XXXXXXXXXX"
  if (digits.length > 10 && digits.startsWith("0091")) digits = digits.slice(4);
  if (digits.length > 10 && digits.startsWith("91")) digits = digits.slice(2);
  // Trunk-dialled "0XXXXXXXXXX"
  if (digits.length > 10 && digits.startsWith("0")) digits = digits.replace(/^0+/, "");
  return digits.slice(0, 10);
}

const FieldControl = ({
  skin,
  field,
  value,
  required,
  disabled,
  error,
  options,
  optionsLoading,
  onFocus,
  onChange,
}: {
  skin: LeadFormSkinDefinition;
  field: LeadFormFieldSchema;
  value: unknown;
  required: boolean;
  disabled: boolean;
  error?: string;
  options: LeadFormOption[];
  optionsLoading: boolean;
  onFocus: () => void;
  onChange: (value: unknown) => void;
}) => {
  const c = skin.classes;
  const fieldClass = cn(c.field, error && c.fieldInvalid);
  const type = String(field.type ?? "text");
  const str = value == null ? "" : String(value);

  /**
   * The Indian mobile is capped at 10 digits HERE, in the renderer, so it holds for every
   * skin. A skin contributes class names and nothing else — putting an input constraint in
   * one would mean the next skin silently loses it.
   *
   * Keyed off the catalog rather than the field id or its label: an author may call the
   * field anything, but only the catalog says "this is the identity number".
   */
  const isIndianMobile = field.catalog === "mobile" && type === "tel";

  const label = (
    <label htmlFor={field.id} className={c.label}>
      {field.label ?? field.id}
      {required && <span className={c.requiredMark}>*</span>}
    </label>
  );

  let control: React.ReactNode;

  if (type === "select" || type === "search-select") {
    /**
     * The same dropdown Lead Drop 9 uses, not a native <select>.
     *
     * Two reasons it is this component rather than a styled `<select>`: a native select's
     * popup list is drawn by the OS and cannot be styled at all, so the menu would never
     * match the skin however much CSS the trigger carried; and Radix's own Select mounts
     * `react-remove-scroll`, which locks WINDOW scroll while open — on an inline form that
     * freezes the whole page until the visitor picks something.
     *
     * `triggerClassName` receives the skin's own field class, so the box keeps whatever
     * border, padding and font the skin defines while the menu stays consistent.
     */
    control = (
      <PopoverSelect
        id={field.id}
        value={str}
        options={options.map((o) => ({ value: o.value, label: o.label }))}
        disabled={disabled || optionsLoading}
        placeholder={optionsLoading ? "Loading…" : (field.placeholder ?? "Select…")}
        ariaLabel={field.label ?? field.id}
        // A native select fires focus; this one opens. Same moment, same intent — it is what
        // the fill-start event has always meant.
        onOpenChange={(open) => {
          if (open) onFocus();
        }}
        onValueChange={onChange}
        triggerClassName={fieldClass}
      />
    );
  } else if (type === "radio") {
    control = (
      <div className="flex flex-wrap gap-4 py-1" onFocus={onFocus}>
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 font-poppins text-[13px] text-[#101112]">
            <input
              type="radio"
              name={field.id}
              value={o.value}
              checked={str === o.value}
              disabled={disabled}
              onChange={() => onChange(o.value)}
              className="h-4 w-4 accent-[var(--lf-primary)]"
            />
            {o.label}
          </label>
        ))}
      </div>
    );
  } else if (type === "checkbox") {
    // A lone checkbox is a boolean (consent, opt-in), not a multi-select.
    control = (
      <label className="flex items-center gap-2 font-poppins text-[13px] text-[#101112]">
        <input
          type="checkbox"
          checked={value === true || str === "true"}
          disabled={disabled}
          onFocus={onFocus}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded accent-[var(--lf-primary)]"
        />
        {field.label ?? field.id}
        {required && <span className={c.requiredMark}>*</span>}
      </label>
    );
  } else if (type === "textarea") {
    control = (
      <textarea
        id={field.id}
        rows={3}
        value={str}
        disabled={disabled}
        placeholder={field.placeholder}
        onFocus={onFocus}
        onChange={(e) => onChange(e.target.value)}
        className={fieldClass}
      />
    );
  } else {
    control = (
      <input
        id={field.id}
        type={type === "tel" || type === "email" || type === "date" || type === "number" ? type : "text"}
        value={str}
        disabled={disabled}
        placeholder={field.placeholder}
        // `tel` gets a numeric keypad on mobile; the server owns actual validity.
        inputMode={type === "tel" ? "numeric" : undefined}
        // Belt to the sanitiser's braces: this alone would not survive a paste, and the
        // sanitiser alone would not stop the browser showing an 11th character mid-keystroke.
        maxLength={isIndianMobile ? 10 : undefined}
        onFocus={onFocus}
        onChange={(e) =>
          onChange(isIndianMobile ? normalizeIndianMobile(e.target.value) : e.target.value)
        }
        className={fieldClass}
      />
    );
  }

  return (
    <div>
      {type !== "checkbox" && label}
      {control}
      {error && <p className={c.errorText}>{error}</p>}
      {!error && field.optionsSource && !optionsLoading && options.length === 0 && (
        // Silence here would look like a broken control. Say so plainly.
        <p className={c.helpText}>No options available.</p>
      )}
    </div>
  );
};

export default ConfigLeadForm;
